import { useState } from "react";
import { toast } from "sonner";
import { useLocation, Link } from "react-router-dom";
import ImageCarousel from "../components/ImageCarousel";
import Rating from "../components/Rating";
import Loader from "../components/Loader";
import Message from "../components/Message";
import Meta from "../components/Meta";
import {
  useGetUserPurchaseProductQuery,
} from "../slices/usersApiSlice";

function PurchaseProductScreen() {
  const location = useLocation();
  const { userId, productId, purchaseId } = location.state || {};
  const [downloadingMap, setDownloadingMap] = useState({});

  const {
    data: product,
    isLoading,
    error,
    refetch,
  } = useGetUserPurchaseProductQuery({ userId, productId, purchaseId }, { skip: !userId || !productId });
  const canRetry = (index) => new Date(product?.downloadRetryUntil?.[index] || 0) > new Date();

  const handleDownload = async (fileUrl, index) => {
    if (!fileUrl || (product.licenses[index] <= 0 && !canRetry(index))) {
      return toast.warning("No licenses left for this file.");
    }

    try {
      setDownloadingMap((prev) => ({ ...prev, [index]: true }));

      const response = await fetch(`/api/users/purchases/${product._id}/files/${index}/download?prepare=1`, {
        method: "POST", credentials: "same-origin",
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Download failed. Please try again.");
      }
      const contentType = response.headers.get("Content-Type") || "";
      if (!contentType.includes("application/json")) throw new Error("The server did not prepare a download. Please try again.");
      const { filename } = await response.json();
      if (typeof filename !== 'string' || !filename) throw new Error('Download filename is missing.');
      const link = document.createElement('a');
      link.href = `/api/users/purchases/${product._id}/files/${index}/download`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

    } catch (error) {
      toast.error(error.message || "Download failed. Please try again.");
    } finally {
      refetch();
      setDownloadingMap((prev) => ({ ...prev, [index]: false }));
    }
  };

  if (!userId || !productId) {
    return <Message variant="danger">Invalid request. Missing product information.</Message>;
  }

  if (isLoading) return <Loader />;
  if (error) return <Message variant="danger">{error?.data?.message || error.message}</Message>;

  const isFree = Number(product?.purchasePrice || 0) <= 0;

  return (
    <div className="resource-screen min-w-0 space-y-4 [overflow-wrap:anywhere]">
      <Link
        to="/purchases"
        className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 no-underline hover:bg-slate-50"
      >
        Go Back
      </Link>

      <Meta title={product.name} />

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-5">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <ImageCarousel images={product.images} />
          </div>
        </div>

        <div className="min-w-0 lg:col-span-7">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div>
              <h1 className="mb-3 text-2xl font-semibold text-slate-900">{product.name}</h1>
              <p className="mb-2 text-sm text-slate-700">
                <strong>Categories:</strong>{" "}
                {Array.isArray(product.category) ? product.category.join(", ") : product.category}
              </p>
              <div className="mb-2">
                <Rating value={product.rating} text={Number(product.numReviews) > 0 ? `${product.numReviews} review${Number(product.numReviews) === 1 ? "" : "s"}` : "No reviews"} />
              </div>
              <p className="mb-2 text-sm text-slate-700">
                <strong>Price:</strong> {isFree ? "Free" : `$${Number(product.purchasePrice).toFixed(2)}`}
              </p>
              <p className="m-0 text-sm leading-6 text-slate-600">{product.description}</p>
            </div>

            <div className="mt-5 border-t border-slate-100 pt-5">
              <p className="mb-4 text-sm font-medium text-slate-700">
                Status:{" "}
                <strong>
                  {product.licenses.some((lic, index) => lic > 0 || canRetry(index))
                    ? "Licenses Available"
                    : "Out of Licenses"}
                </strong>
              </p>
              {!product.licenses.some((lic, index) => lic > 0 || canRetry(index)) && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
                  You have no licenses remaining for this purchase right now. To download these
                  files again, you will need to acquire additional licenses for the product.
                </div>
              )}

              <p className="mb-3 text-sm text-slate-600">Each download uses one license. You can retry the same file for 15 minutes without using another license.</p>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {product.files.map((file, index) => (
                  <div
                    key={`${product._id || product.productId}-file-${index}`}
                    className="rounded-xl border border-slate-200 bg-slate-50 p-3"
                  >
                    <div className="grid grid-cols-1 gap-3">
                      <p className="m-0 break-words text-sm font-medium text-slate-700" title={file}>
                        {file}
                      </p>
                      <div className="flex flex-col items-center justify-center gap-3 text-center sm:flex-row sm:justify-between sm:text-left">
                        <p className="m-0 whitespace-nowrap text-xs text-slate-600">
                          Licenses: <strong>{product.licenses[index]}</strong>
                        </p>
                        <button
                          type="button"
                          onClick={() => handleDownload(file, index)}
                          disabled={downloadingMap[index] || (product.licenses[index] <= 0 && !canRetry(index))}
                          className="inline-flex min-w-[152px] items-center justify-center rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                        >
                          {downloadingMap[index] ? "Downloading..." : "Download"}
                        </button>
                      </div>
                    </div>
                    {product.licenses[index] <= 0 && !canRetry(index) && (
                      <p className="mt-2 text-xs text-amber-700">
                        No licenses left for this file. Consider acquiring more licenses to
                        download it again.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PurchaseProductScreen;

